import { HTMLAttributes, useMemo } from 'react'

import type { ParticipantClickEvent, TrackReferenceOrPlaceholder } from '@livekit/components-core'
import { setupParticipantTile } from '@livekit/components-core'
import { Track } from 'livekit-client'

import { useEnsureTrackRef } from '@/src/context'
import { mergeProps } from '@/src/mergeProps'

import { useIsMuted } from '@/src/hooks/media/useIsMuted'
import { useIsSpeaking } from '@/src/hooks/media/useIsSpeaking'
import { useFacingMode } from '@/src/hooks/useFacingMode'

import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

/** @public */
export interface UseParticipantTileProps<T extends HTMLElement> extends HTMLAttributes<T> {
  /** The track reference to display. */
  trackRef?: TrackReferenceOrPlaceholder
  disableSpeakingIndicator?: boolean
  onParticipantClick?: (event: ParticipantClickEvent) => void
  htmlProps: HTMLAttributes<T>
}

/**
 * The `useParticipantTile` hook is used to implement the `ParticipantTile` and returns the props needed to render the tile.
 * @remarks
 * The returned props include many data attributes that are useful for CSS styling purposes because they
 * indicate the state of the participant and the track.
 * For example: `data-lk-audio-muted`, `data-lk-video-muted`, `data-lk-speaking`, `data-lk-local-participant`, `data-lk-source`, `data-lk-facing-mode`.
 * @public
 */
export function useParticipantTile<T extends HTMLElement>({
  trackRef,
  onParticipantClick,
  disableSpeakingIndicator,
  htmlProps,
}: UseParticipantTileProps<T>) {
  const trackReference = useEnsureTrackRef(trackRef)

  const micTrack = trackReference.participant.getTrackPublication(Track.Source.Microphone)
  const micRef = useMemo(
    () => ({
      participant: trackReference.participant,
      source: Track.Source.Microphone,
      publication: micTrack,
    }),
    [micTrack, trackReference.participant],
  )
  const isVideoMuted = useIsMuted(trackReference)
  const isAudioMuted = useIsMuted(micRef)
  const isSpeaking = useIsSpeaking(trackReference.participant)
  const facingMode = useFacingMode(trackReference)

  const isSpeakingIndicator = useMemo(
    () => (disableSpeakingIndicator === true ? false : isSpeaking),
    [disableSpeakingIndicator, isSpeaking],
  )

  const { primary_color } = useAppSelector(selectBranding)

  const mergedProps = useMemo(() => {
    const { className } = setupParticipantTile()
    return mergeProps(htmlProps, {
      className,
      onClick: (event: React.MouseEvent<T, MouseEvent>) => {
        htmlProps.onClick?.(event)
        if (typeof onParticipantClick === 'function') {
          const track =
            trackReference.publication ??
            trackReference.participant.getTrackPublication(trackReference.source)
          onParticipantClick({ participant: trackReference.participant, track })
        }
      },
      style: {
        ...htmlProps.style,
        transitionProperty: 'border',
        transitionDelay: '0.1s',
        transitionDuration: '0.4s',
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: isSpeakingIndicator
          ? htmlProps.style?.borderColor ?? primary_color ?? 'var(--lk-accent2)'
          : 'transparent',
      },
    })
  }, [
    htmlProps,
    onParticipantClick,
    trackReference.publication,
    trackReference.source,
    trackReference.participant,
    isSpeakingIndicator,
    primary_color,
  ])

  return {
    elementProps: {
      'data-lk-audio-muted': isAudioMuted,
      'data-lk-video-muted': isVideoMuted,
      'data-lk-speaking': isSpeakingIndicator,
      'data-lk-local-participant': trackReference.participant.isLocal,
      'data-lk-source': trackReference.source,
      'data-lk-facing-mode': facingMode,
      ...mergedProps,
    } as React.HTMLAttributes<T>,
  }
}
